/**
 * WebSocket API Service - интеграция с backend WebSocket endpoints
 * Event Master v1.5 - Backend Integration
 * День 10: WebSocket Module (5 endpoints)
 */

import { httpClient } from '../http-client.service';
import {
  ApiResponse,
} from '../../types/shared-types';
import {
  RealtimeDataStream,
  CreateRealtimeStreamDto,
  UpdateRealtimeStreamDto,
  BalanceStreamsRequest,
  StreamStatisticsResponse,
  GetConnectionsRequest,
  ConnectionsStatisticsResponse,
  SubscriptionsStatisticsResponse,
  MessagesStatisticsResponse,
  StreamsStatisticsResponse,
  UserActivityResponse,
  GetTopUsersRequest,
  TopUsersResponse,
  TopChannelsResponse,
} from '../../types/websocket';

const BASE_URL = '/websocket';

// ===== WEB SOCKET ТИПЫ =====

export interface WebSocketConnection {
  id: string;
  userId: string;
  status: 'connected' | 'disconnected' | 'connecting';
  connectedAt: Date;
  lastActivity: Date;
  channels: string[];
}

export interface WebSocketMessage {
  id: string;
  type: 'notification' | 'chat' | 'event' | 'analytics' | 'system';
  channel: string;
  data: any;
  timestamp: Date;
  sender?: string;
  /**
   * AD-514. Имя автора в истории чата: сервер отдаёт его здесь
   * (`WebSocketChatController.getMessages`), а не в `data`.
   */
  senderName?: string;
  recipient?: string;
}

export interface WebSocketChannel {
  id: string;
  name: string;
  type: 'public' | 'private' | 'direct';
  participants: string[];
  lastMessage?: WebSocketMessage;
  unreadCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface WebSocketEvent {
  type: 'connect' | 'disconnect' | 'message' | 'error' | 'reconnect';
  data?: any;
  timestamp: Date;
}

// ===== WEB SOCKET СОЕДИНЕНИЯ =====

/**
 * Получение активных WebSocket соединений
 */
export const getWebSocketConnections = async (): Promise<WebSocketConnection[]> => {
  const { data } = await httpClient.get<ApiResponse<WebSocketConnection[]>>(
    `${BASE_URL}/connections`
  );
  return data.data;
};

/**
 * Получение информации о WebSocket соединении
 */
export const getWebSocketConnection = async (connectionId: string): Promise<WebSocketConnection> => {
  const { data } = await httpClient.get<ApiResponse<WebSocketConnection>>(
    `${BASE_URL}/connections/${connectionId}`
  );
  return data.data;
};

/**
 * Установка WebSocket соединения
 */
export const connectWebSocket = async (channels?: string[]): Promise<WebSocketConnection> => {
  const { data } = await httpClient.post<ApiResponse<WebSocketConnection>>(
    `${BASE_URL}/connect`,
    { channels }
  );
  return data.data;
};

/**
 * Отключение WebSocket соединения
 */
export const disconnectWebSocket = async (connectionId: string): Promise<void> => {
  await httpClient.post(`${BASE_URL}/disconnect/${connectionId}`);
};

// ===== WEB SOCKET КАНАЛЫ =====

/**
 * Получение списка WebSocket каналов
 */
export const getWebSocketChannels = async (): Promise<WebSocketChannel[]> => {
  const { data } = await httpClient.get<ApiResponse<WebSocketChannel[]>>(
    `${BASE_URL}/channels`
  );
  return data.data;
};

/**
 * Получение информации о WebSocket канале
 */
export const getWebSocketChannel = async (channelId: string): Promise<WebSocketChannel> => {
  const { data } = await httpClient.get<ApiResponse<WebSocketChannel>>(
    `${BASE_URL}/channels/${channelId}`
  );
  return data.data;
};

/**
 * Подписка на WebSocket канал
 */
export const subscribeToChannel = async (channelId: string): Promise<WebSocketChannel> => {
  const { data } = await httpClient.post<ApiResponse<WebSocketChannel>>(
    `${BASE_URL}/channels/${channelId}/subscribe`
  );
  return data.data;
};

/**
 * Отписка от WebSocket канала
 */
export const unsubscribeFromChannel = async (channelId: string): Promise<void> => {
  await httpClient.post(`${BASE_URL}/channels/${channelId}/unsubscribe`);
};

// ===== WEB SOCKET СООБЩЕНИЯ =====

/**
 * Получение сообщений из WebSocket канала
 */
export const getWebSocketMessages = async (channelId: string, limit = 50): Promise<WebSocketMessage[]> => {
  const { data } = await httpClient.get<ApiResponse<WebSocketMessage[]>>(
    `${BASE_URL}/channels/${channelId}/messages`,
    { params: { limit } }
  );
  return data.data;
};

/**
 * Отправка сообщения в WebSocket канал
 */
export const sendWebSocketMessage = async (channelId: string, message: Partial<WebSocketMessage>): Promise<WebSocketMessage> => {
  const { data } = await httpClient.post<ApiResponse<WebSocketMessage>>(
    `${BASE_URL}/channels/${channelId}/messages`,
    message
  );
  return data.data;
};

/**
 * Получение непрочитанных сообщений
 */
export const getUnreadMessages = async (): Promise<WebSocketMessage[]> => {
  const { data } = await httpClient.get<ApiResponse<WebSocketMessage[]>>(
    `${BASE_URL}/messages/unread`
  );
  return data.data;
};

/**
 * Отметка сообщений как прочитанные
 */
export const markMessagesAsRead = async (messageIds: string[]): Promise<void> => {
  await httpClient.post(`${BASE_URL}/messages/read`, { messageIds });
};

// ===== WEB SOCKET УВЕДОМЛЕНИЯ =====

/**
 * Получение real-time уведомлений
 */
export const getRealTimeNotifications = async (): Promise<WebSocketMessage[]> => {
  const { data } = await httpClient.get<ApiResponse<WebSocketMessage[]>>(
    `${BASE_URL}/notifications`
  );
  return data.data;
};

/**
 * Подписка на уведомления
 */
export const subscribeToNotifications = async (types?: string[]): Promise<void> => {
  await httpClient.post(`${BASE_URL}/notifications/subscribe`, { types });
};

/**
 * Отписка от уведомлений
 */
export const unsubscribeFromNotifications = async (): Promise<void> => {
  await httpClient.post(`${BASE_URL}/notifications/unsubscribe`);
};

// ===== WEB SOCKET ЧАТ =====

/**
 * История сообщений канала.
 *
 * AD-480: ошибку больше не глотаем. С появлением персиста история — реальные
 * данные под допуском (посторонний получает 403), а `catch { return [] }`
 * рисовал и отказ, и сбой одинаково — «сообщений нет». Он же делал ветку
 * ошибки в `useChatMessages` недостижимой.
 */
export const getChatMessages = async (chatId: string, limit = 50): Promise<WebSocketMessage[]> => {
  const response = await httpClient.get<ApiResponse<WebSocketMessage[]>>(
    `${BASE_URL}/chat/${chatId}/messages`,
    { params: { limit } }
  );
  const payload = response.data?.data ?? (response.data as unknown as WebSocketMessage[]);
  return Array.isArray(payload) ? payload : [];
};

/**
 * Отправка чат-сообщения
 */
export const sendChatMessage = async (chatId: string, message: Partial<WebSocketMessage>): Promise<WebSocketMessage> => {
  const response = await httpClient.post<ApiResponse<WebSocketMessage>>(
    `${BASE_URL}/chat/${chatId}/messages`,
    message
  );
  return (response.data?.data ?? response.data) as WebSocketMessage;
};

/**
 * Отметить чат как прочитанный (сбросить unreadCount для текущего пользователя)
 */
export const markChatAsRead = async (chatId: string): Promise<void> => {
  try {
    await httpClient.post(`${BASE_URL}/chat/${chatId}/read`);
  } catch {
    // fire-and-forget: не прерываем UX если запрос не прошёл
  }
};

/**
 * Список активных каналов.
 *
 * AD-480: как и у истории, отказ и сбой больше не подменяются пустым списком —
 * экран чатов уже держит ветку `error`, но она была недостижима.
 */
export const getActiveChats = async (): Promise<WebSocketChannel[]> => {
  const response = await httpClient.get<ApiResponse<WebSocketChannel[]>>(
    `${BASE_URL}/chat/active`
  );
  // Backend возвращает { success: true, data: [...], pagination }
  const payload = response.data?.data ?? (response.data as unknown as WebSocketChannel[]);
  return Array.isArray(payload) ? payload : [];
};

// ===== WEB SOCKET СОБЫТИЯ =====

/**
 * Получение real-time событий
 */
export const getRealTimeEvents = async (): Promise<WebSocketMessage[]> => {
  const { data } = await httpClient.get<ApiResponse<WebSocketMessage[]>>(
    `${BASE_URL}/events`
  );
  return data.data;
};

/**
 * Подписка на события
 */
export const subscribeToEvents = async (eventTypes?: string[]): Promise<void> => {
  await httpClient.post(`${BASE_URL}/events/subscribe`, { eventTypes });
};

/**
 * Отписка от событий
 */
export const unsubscribeFromEvents = async (): Promise<void> => {
  await httpClient.post(`${BASE_URL}/events/unsubscribe`);
};

// ===== WEB SOCKET АНАЛИТИКА =====

/**
 * Получение real-time аналитики
 */
export const getRealTimeAnalytics = async (): Promise<WebSocketMessage[]> => {
  const { data } = await httpClient.get<ApiResponse<WebSocketMessage[]>>(
    `${BASE_URL}/analytics`
  );
  return data.data;
};

/**
 * Подписка на аналитику
 */
export const subscribeToAnalytics = async (metrics?: string[]): Promise<void> => {
  await httpClient.post(`${BASE_URL}/analytics/subscribe`, { metrics });
};

/**
 * Отписка от аналитики
 */
export const unsubscribeFromAnalytics = async (): Promise<void> => {
  await httpClient.post(`${BASE_URL}/analytics/unsubscribe`);
};

// ===== УПРАВЛЕНИЕ ПОТОКАМИ ДАННЫХ =====

/**
 * Создание потока данных
 * POST /api/websocket/streams
 */
export const createRealtimeStream = async (
  payload: CreateRealtimeStreamDto
): Promise<RealtimeDataStream> => {
  const { data } = await httpClient.post<ApiResponse<RealtimeDataStream>>(
    `${BASE_URL}/streams`,
    payload
  );
  return data.data;
};

/**
 * Получение активных потоков
 * GET /api/websocket/streams/active
 */
export const getActiveStreams = async (): Promise<RealtimeDataStream[]> => {
  const { data } = await httpClient.get<ApiResponse<RealtimeDataStream[]>>(
    `${BASE_URL}/streams/active`
  );
  return data.data;
};

/**
 * Обновление потока данных
 * PUT /api/websocket/streams/:streamId
 */
export const updateRealtimeStream = async (
  streamId: string,
  payload: UpdateRealtimeStreamDto
): Promise<RealtimeDataStream> => {
  const { data } = await httpClient.put<ApiResponse<RealtimeDataStream>>(
    `${BASE_URL}/streams/${streamId}`,
    payload
  );
  return data.data;
};

/**
 * Удаление потока данных
 * DELETE /api/websocket/streams/:streamId
 */
export const deleteRealtimeStream = async (streamId: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/streams/${streamId}`);
};

/**
 * Пауза потока данных
 * POST /api/websocket/streams/:streamId/pause
 */
export const pauseRealtimeStream = async (streamId: string): Promise<RealtimeDataStream> => {
  const { data } = await httpClient.post<ApiResponse<RealtimeDataStream>>(
    `${BASE_URL}/streams/${streamId}/pause`
  );
  return data.data;
};

/**
 * Возобновление потока данных
 * POST /api/websocket/streams/:streamId/resume
 */
export const resumeRealtimeStream = async (streamId: string): Promise<RealtimeDataStream> => {
  const { data } = await httpClient.post<ApiResponse<RealtimeDataStream>>(
    `${BASE_URL}/streams/${streamId}/resume`
  );
  return data.data;
};

/**
 * Балансировка подписок
 * POST /api/websocket/streams/balance
 */
export const balanceStreams = async (
  params?: BalanceStreamsRequest
): Promise<RealtimeDataStream[]> => {
  const queryParams = new URLSearchParams();
  
  if (params?.maxActiveStreams) {
    queryParams.append('maxActiveStreams', params.maxActiveStreams.toString());
  }

  const url = `${BASE_URL}/streams/balance${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const { data } = await httpClient.post<ApiResponse<RealtimeDataStream[]>>(url);
  
  return data.data;
};

/**
 * Получение статистики потоков
 * GET /api/websocket/streams/statistics
 */
export const getStreamStatistics = async (): Promise<StreamStatisticsResponse> => {
  const { data } = await httpClient.get<ApiResponse<StreamStatisticsResponse>>(
    `${BASE_URL}/streams/statistics`
  );
  return data.data;
};

// ===== АНАЛИТИКА WEB SOCKET =====

/**
 * Статистика подключений
 * GET /api/websocket/analytics/connections
 */
export const getConnectionsStatistics = async (
  params?: GetConnectionsRequest
): Promise<ConnectionsStatisticsResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.userId) {
    queryParams.append('userId', params.userId);
  }
  if (params?.startDate) {
    queryParams.append('startDate', params.startDate);
  }
  if (params?.endDate) {
    queryParams.append('endDate', params.endDate);
  }

  const url = `${BASE_URL}/analytics/connections${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const { data } = await httpClient.get<ApiResponse<ConnectionsStatisticsResponse>>(url);
  
  return data.data;
};

/**
 * Статистика подписок
 * GET /api/websocket/analytics/subscriptions
 */
export const getSubscriptionsStatistics = async (): Promise<SubscriptionsStatisticsResponse> => {
  const { data } = await httpClient.get<ApiResponse<SubscriptionsStatisticsResponse>>(
    `${BASE_URL}/analytics/subscriptions`
  );
  return data.data;
};

/**
 * Статистика сообщений
 * GET /api/websocket/analytics/messages
 */
export const getMessagesStatistics = async (): Promise<MessagesStatisticsResponse> => {
  const { data } = await httpClient.get<ApiResponse<MessagesStatisticsResponse>>(
    `${BASE_URL}/analytics/messages`
  );
  return data.data;
};

/**
 * Статистика потоков (для аналитики)
 * GET /api/websocket/analytics/streams
 */
export const getStreamsStatistics = async (): Promise<StreamsStatisticsResponse> => {
  const { data } = await httpClient.get<ApiResponse<StreamsStatisticsResponse>>(
    `${BASE_URL}/analytics/streams`
  );
  return data.data;
};

/**
 * Активность пользователя
 * GET /api/websocket/analytics/user-activity
 */
export const getUserActivity = async (): Promise<UserActivityResponse> => {
  const { data } = await httpClient.get<ApiResponse<UserActivityResponse>>(
    `${BASE_URL}/analytics/user-activity`
  );
  return data.data;
};

/**
 * Топ активных пользователей
 * GET /api/websocket/analytics/top-users
 */
export const getTopUsers = async (params?: GetTopUsersRequest): Promise<TopUsersResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.limit) {
    queryParams.append('limit', params.limit.toString());
  }

  const url = `${BASE_URL}/analytics/top-users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const { data } = await httpClient.get<ApiResponse<TopUsersResponse>>(url);
  
  return data.data;
};

/**
 * Топ каналов
 * GET /api/websocket/analytics/top-channels
 */
export const getTopChannels = async (): Promise<TopChannelsResponse> => {
  const { data } = await httpClient.get<ApiResponse<TopChannelsResponse>>(
    `${BASE_URL}/analytics/top-channels`
  );
  return data.data;
};

// ===== WEB SOCKET СТАТУС =====

/**
 * Получение статуса WebSocket сервера
 */
export const getWebSocketStatus = async (): Promise<{
  status: 'online' | 'offline' | 'maintenance';
  connections: number;
  channels: number;
  uptime: number;
}> => {
  const { data } = await httpClient.get<ApiResponse<{
    status: 'online' | 'offline' | 'maintenance';
    connections: number;
    channels: number;
    uptime: number;
  }>>(`${BASE_URL}/status`);
  return data.data;
};

/**
 * Получение статистики WebSocket
 */
export const getWebSocketStats = async (): Promise<{
  totalConnections: number;
  activeConnections: number;
  totalMessages: number;
  totalChannels: number;
  averageResponseTime: number;
}> => {
  const { data } = await httpClient.get<ApiResponse<{
    totalConnections: number;
    activeConnections: number;
    totalMessages: number;
    totalChannels: number;
    averageResponseTime: number;
  }>>(`${BASE_URL}/stats`);
  return data.data;
};

// ===== EXPORT =====

const websocketApi = {
  // Connections
  getWebSocketConnections,
  getWebSocketConnection,
  connectWebSocket,
  disconnectWebSocket,
  
  // Channels
  getWebSocketChannels,
  getWebSocketChannel,
  subscribeToChannel,
  unsubscribeFromChannel,
  
  // Messages
  getWebSocketMessages,
  sendWebSocketMessage,
  getUnreadMessages,
  markMessagesAsRead,
  
  // Notifications
  getRealTimeNotifications,
  subscribeToNotifications,
  unsubscribeFromNotifications,
  
  // Chat
  getChatMessages,
  sendChatMessage,
  getActiveChats,
  markChatAsRead,
  
  // Events
  getRealTimeEvents,
  subscribeToEvents,
  unsubscribeFromEvents,
  
  // Analytics
  getRealTimeAnalytics,
  subscribeToAnalytics,
  unsubscribeFromAnalytics,
  
  // Streams Management
  createRealtimeStream,
  getActiveStreams,
  updateRealtimeStream,
  deleteRealtimeStream,
  pauseRealtimeStream,
  resumeRealtimeStream,
  balanceStreams,
  getStreamStatistics,
  
  // WebSocket Analytics
  getConnectionsStatistics,
  getSubscriptionsStatistics,
  getMessagesStatistics,
  getStreamsStatistics,
  getUserActivity,
  getTopUsers,
  getTopChannels,
  
  // Status
  getWebSocketStatus,
  getWebSocketStats,
};
export default websocketApi;
